import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/'

let authToken: string | null = null;

/**
 * Crea y configura una instancia de Axios para su uso en la aplicación cliente.
 * Incluye interceptores básicos para añadir el token de autorización y un
 * manejo uniforme de errores.
 */
function createApiClient(baseURL: string = DEFAULT_BASE_URL): AxiosInstance {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30_000,
  });

  client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      if (!config.headers) config.headers = {};
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(normalizeError(error))
  );

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        authToken = null;
        sessionStorage.removeItem('jwt_token');
        window.location.href = import.meta.env.VITE_LOGOUT_URL || import.meta.env.VITE_LOGIN_URL || '/';
      }
      return Promise.reject(normalizeError(error));
    }
  );

  return client;
}

/**
 * Normaliza un AxiosError para exponer una estructura mínima útil para la UI.
 */
function normalizeError(error: AxiosError) {
  const normalized: {
    message: string;
    status?: number;
    data?: unknown;
  } = {
    message: error.message || 'Error de red',
  };

  if (error.response) {
    normalized.status = error.response.status;
    normalized.data = error.response.data;
    // Priorizar mensajes específicos del servidor si existen
    if (
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in (error.response.data as any)
    ) {
      normalized.message = (error.response.data as any).message;
    }
  }

  return normalized;
}

const api = createApiClient();

/**
 * Establece el token de autorización que se añadirá a las peticiones.
 * Para eliminar el token pasar `null`.
 */
function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Permite reemplazar la instancia interna de Axios (útil para pruebas).
 */
function replaceApiInstance(instance: AxiosInstance): void {
  // Reemplaza todas las referencias exportadas que dependen de la instancia.
  // En este módulo mantenemos la instancia en `api`, por lo que no es posible
  // reasignar `api` sin exportar un setter. Esta función devuelve nada pero
  // existe para cumplir con posibles necesidades de testing/overrides.
  // Si se necesita una implementación que reemplace realmente la instancia,
  // convertir `api` en función o en getter sería necesario.
  void instance;
}

export { api, setAuthToken, replaceApiInstance, normalizeError };
export default api;